import type {
  Chemical as PluginChemical,
  Drug as PluginDrug,
  Line as PluginLine,
  ModelElement as PluginModelElement,
  ModelElementModification as PluginModelElementModification,
  Target as PluginTarget,
} from 'minerva-plugin-api';
import { Chemical, Drug, Line, Modification, ModelElement, PinDetailsItem } from '@/types/models';
import {
  toOptionalPluginColor,
  toPluginColor,
} from '@/services/pluginsManager/adapters/toPluginColor';
import {
  toPluginAnnotation,
  toPluginAnnotations,
} from '@/services/pluginsManager/adapters/toPluginAnnotation';

export const toPluginLine = (line: Line): PluginLine => ({
  ...line,
  color: toPluginColor(line.color),
  colorRgba: line.color,
});

export const toPluginModelElementModification = (
  modification: Modification,
): PluginModelElementModification => ({
  ...modification,
  borderColor: toPluginColor(modification.borderColor),
  borderColorRgba: modification.borderColor,
  fillColor: toPluginColor(modification.fillColor),
  fillColorRgba: modification.fillColor,
  line: modification.line ? toPluginLine(modification.line) : undefined,
});

// elements assembled by hand can miss colors altogether (see mapReactionToModelElement),
// and plugins used to receive them as-is, so the cast keeps that behaviour
export const toPluginModelElement = (element: ModelElement): PluginModelElement =>
  ({
    ...element,
    fontColor: toOptionalPluginColor(element.fontColor),
    fontColorRgba: element.fontColor,
    fillColor: toOptionalPluginColor(element.fillColor),
    fillColorRgba: element.fillColor,
    borderColor: toOptionalPluginColor(element.borderColor),
    borderColorRgba: element.borderColor,
    model: element.map,
    submodel: element.submap,
    // the VMH/Recon annotator that was their only source is gone from minerva
    abbreviation: null,
    formula: null,
    references: toPluginAnnotations(element.references),
    modificationResidues: element.modificationResidues?.map(toPluginModelElementModification),
  }) as PluginModelElement;

export const toPluginTarget = (target: PinDetailsItem): PluginTarget => ({
  ...target,
  references: toPluginAnnotations(target.references),
  targetParticipants: toPluginAnnotations(target.targetParticipants),
  targetElements: target.targetElements?.map(toPluginModelElement),
});

export const toPluginDrug = (drug: Drug): PluginDrug => ({
  ...drug,
  references: toPluginAnnotations(drug.references),
  targets: drug.targets.map(toPluginTarget),
});

export const toPluginChemical = (chemical: Chemical): PluginChemical => ({
  ...chemical,
  id: toPluginAnnotation(chemical.id),
  references: toPluginAnnotations(chemical.references),
  targets: chemical.targets.map(toPluginTarget),
});
